import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import config from '@/config';
import { useStore } from '@/store/useStore';
import { getUnifiedModule } from '@/utilities/logger/amplitude/initialise';
import {
  AMPLITUDE_COOKIES,
  getAmplitudeCookieValue,
  setAmplitudeDeviceIdCookie,
} from '@/utilities/logger/amplitude/amplitudeCookies';

let lastSyncedUserId: string | null = null;

const parseSessionId = (value: string): number | undefined => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Binds Amplitude analytics identity to the logged-in user and lobby cookies.
 * Mirrors launcher `useUserTrackingSetup` init options (`userId`, `deviceId`, `sessionId`).
 */
export const syncAmplitudeIdentity = async (userId: string): Promise<void> => {
  if (!userId || userId === lastSyncedUserId) {
    return;
  }

  try {
    const mod = await getUnifiedModule();
    const amplitudeDeviceId = getAmplitudeCookieValue(
      AMPLITUDE_COOKIES.AMPLITUDE_ID,
    );
    const ampDeviceId = getAmplitudeCookieValue(
      AMPLITUDE_COOKIES.AMP_DEVICE_ID,
    );
    const sessionId = parseSessionId(
      getAmplitudeCookieValue(AMPLITUDE_COOKIES.AMP_SESSION_ID),
    );

    mod.setUserId(userId);

    const deviceId = amplitudeDeviceId || ampDeviceId;
    if (deviceId) {
      mod.setDeviceId(deviceId);
    } else {
      const generatedDeviceId = crypto.randomUUID();
      mod.setDeviceId(generatedDeviceId);
      setAmplitudeDeviceIdCookie(
        generatedDeviceId,
        (config as { rootDomain?: string }).rootDomain,
      );
    }

    if (sessionId !== undefined) {
      mod.setSessionId(sessionId);
    }

    lastSyncedUserId = userId;
  } catch {
    /* swallow — analytics must never break the app */
  }
};

/** Test-only helper to reset module-level sync state between tests. */
export const __resetAmplitudeIdentityForTests = (): void => {
  lastSyncedUserId = null;
};

/**
 * Keeps Amplitude user/device/session identity aligned with the active session.
 * Call from App alongside `useInitAmpli`.
 */
export const useAmplitudeIdentitySync = (): void => {
  const userId = useStore(useShallow((s) => s.userSessionContext.userId));

  useEffect(() => {
    if (!userId) return;
    void syncAmplitudeIdentity(userId);
  }, [userId]);
};
